import {useEffect, useState} from 'react';
import {
  Container,
  Row,
  Col,
} from 'reactstrap';

import {GroupNode, GroupNodeConnection} from 'src/types';
import {getGroupingNodes, getGroupingNodeReferences} from 'src/api/groups';
import {isDefined} from 'src/types/util';
import {
  LoadingPlaceholder,
  SizeContent,
  MaterializedViewAlert,
} from 'src/components';
import {FixedDiagram, WorkingOnIt} from 'src/components';
import {OverviewRelationshipList} from 'src/components/graph/OverviewRelationshipList';

type ReactState = {loading: boolean, groups: Array<GroupNode>, connections: Array<GroupNodeConnection>}

type RelationshipAnalysis = {
  fromId: string,
  toId: string,
}

export const ApplicationGraph = (props: {definitionId: string | undefined}) => {
  const [currentAnalysis, setCurrentAnalysis] = useState<RelationshipAnalysis>();
  const [
    {
      loading,
      groups,
      connections,
    },
    setFetchedData,
  ] = useState<ReactState>({
    loading: true,
    groups: [],
    connections: [],
  });

  const getApplications = async (definitionId: string) => {
    const response = await getGroupingNodes(null, definitionId, {page: 0, pageSize: 1000});
    if (response.status === 'success') {
      const groups = response.data;
      if (groups) {
        const connections = (
          await Promise.all(
              groups.map(({id}) => getGroupingNodeReferences(id)))
        ).filter(isDefined).flat();

        setFetchedData({
          groups: groups.map((x) => ({...x, name: `${x.name}`})),
          // -${x.name.split('').sort(() => 0.5 - Math.random()).join('')}
          connections,
          loading: false,
        });
      }
    }
  };

  useEffect(() => {
    if (props.definitionId) {
      getApplications(props.definitionId);
    }
  }, [props.definitionId]);


  if (loading) {
    return <LoadingPlaceholder />;
  } else if (!groups?.length) {
    return (
      <WorkingOnIt gotData={async () => props.definitionId && await getApplications(props.definitionId)}>
        <span>No Application Data</span>
      </WorkingOnIt>
    );
  }

  const {groupsMap, edgesMap} = parseGroups(groups, connections);

  return (
    <>
      <MaterializedViewAlert dataAvailable={() => window.location.reload()}/>
      <Container fluid style={{height: '100%'}}>
        <Row style={{height: '100%'}}>
          <Col style={{height: '100%', position: 'relative'}}>
            <SizeContent>
              {({height, width}) => (
                <FixedDiagram
                  nodes={groupsMap}
                  edges={edgesMap}
                  height={height}
                  width={width}
                  onLinkClicked={(event) => {
                    setCurrentAnalysis({
                      fromId: event.fromId,
                      toId: event.toId,
                    });
                  }}
                />
              )}
            </SizeContent>
          </Col>
        </Row>
        {currentAnalysis && <OverviewRelationshipList isOpen={true} onClose={() => setCurrentAnalysis(undefined)} fromId={currentAnalysis.fromId} toId={currentAnalysis.toId} />}
      </Container>
    </>
  );
};

const parseGroups = (groups: Array<GroupNode>, connections: Array<GroupNodeConnection>) => {
  const groupsMap = groups.reduce((accum, app) => ({
    ...accum,
    [app.id]: {
      data: app,
    },
  }), {});

  const edgesMap: Record<string, {
        data: {count: number},
        id: string
        0: string,
        1: string,
      }> = connections.reduce((accum, {fromGroupId, toGroupId, count, direction}) => ({
        ...accum,
        ['' + fromGroupId + toGroupId]: {
          data: {count, direction},
          id: fromGroupId + toGroupId,
          0: fromGroupId,
          1: toGroupId,
        },
      }), {});

  return {
    groupsMap,
    edgesMap,
  };
};
